import { Dialog, DialogActions, DialogContent, DialogTitle, Stack, Typography } from '@mui/material';
import PaiLink from 'features/ui/base-components/pai-link';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { makeStyles } from 'tss-react/mui';
import PaiButton from '../../ui/base-components/pai-button';
import PaiMenuItem from '../../ui/base-components/pai-menu-item';
import PaiSelect from '../../ui/base-components/pai-select';

interface Props {
  open: boolean;
  loading: boolean;
  selectedDuration: string;

  handleSelectedDurationChange(event: React.ChangeEvent<HTMLInputElement>): void;

  handleClose(): void;

  handleConfirmLogout(): void;
}

const ScheduledLogoutDialog: React.FC<Props> = ({
  open,
  loading,
  selectedDuration,
  handleSelectedDurationChange,
  handleClose,
  handleConfirmLogout,
}) => {
  const { classes } = useStyles();
  const { t } = useTranslation();

  const durationOptions: { label: string; value: string }[] = [
    { label: t('duration30Minutes'), value: '1800' }, // 30 * 60
    { label: t('duration1Hour'), value: '3600' }, // 60 * 60
    { label: t('duration5Hours'), value: '18000' }, // 5 * 60 * 60
    { label: t('durationrestOfDay'), value: '86400' }, // 24 * 60 * 60
  ];

  return (
    <Dialog classes={{ paper: classes.paper }} open={open} onClose={handleClose} fullWidth={true} maxWidth={'xs'} transitionDuration={0}>
      <DialogTitle className={classes.title}>
        <Typography className={classes.titleText}>{t('logoutModaltitle')}</Typography>
      </DialogTitle>
      <DialogContent className={classes.content}>
        <Stack direction={'column'} gap={2}>
          <Typography className={classes.text}>{t('logoutModaldescription')}</Typography>
          <PaiSelect value={selectedDuration} onChange={handleSelectedDurationChange} multiple={false}>
            {durationOptions.map((option) => (
              <PaiMenuItem key={option.value} value={option.value}>
                {option.label}
              </PaiMenuItem>
            ))}
          </PaiSelect>
        </Stack>
      </DialogContent>
      <DialogActions className={classes.actions}>
        <PaiLink text={t('logoutModalcancel')} size={'sm'} underline={'always'} action={handleClose} />
        <PaiButton
          text={t('logoutModalconfirm')}
          size={'small'}
          border={'rounded'}
          action={handleConfirmLogout}
          disabled={parseInt(selectedDuration, 10) <= 0} // Convert string to number before comparison
          loading={loading}
        />
      </DialogActions>
    </Dialog>
  );
};

const useStyles = makeStyles()((theme) => ({
  paper: {
    borderRadius: '16px',
  },
  title: {
    background: 'linear-gradient(to right, #5044FF, #4AA4FF)',
  },
  titleText: {
    fontSize: theme.typography.pxToRem(15),
    fontWeight: 'bold',
    textTransform: 'uppercase',
    color: '#FFF',
    textAlign: 'center',
  },
  content: {
    padding: '20px 24px !important',
    whiteSpace: 'pre-wrap',
  },
  text: {
    fontSize: theme.typography.pxToRem(15),
    whiteSpace: 'pre-wrap',
  },
  actions: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: '16px',
    padding: '0 24px 20px 24px',
  },
}));

export default ScheduledLogoutDialog;
